import type { CycleAttack } from '../../../data/full/avatar';
import { angleOf, unitFromAngle } from '../../../shared/angle';
import { clamp, clamp01, lerp, wrap } from '../../../shared/scalar';
import { type Tempo, barOfTick, isBeatTick } from '../../../shared/tempo';
import { distanceSquared } from '../../../shared/vec';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { hurtEnemy, keepWhere, knockBack, touches } from '../../effects';
import { applyEnemyStatus } from '../../enemy-status';
import type { EnemyState, PlayerState, PropState, SimState, Vec2 } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { preyOf } from './swarm';

export const FAMILIAR = 'familiar';
export const BURN = 'burn';

export const FIRE = 0;
export const WATER = 1;
export const AIR = 2;
export const EARTH = 3;
export const ELEMENTS = 4;

// How far a leg bends from the straight line, as a share of its length.
const BULGE = 0.24;

export function elementOfTick(tick: number, tempo: Tempo): number {
  return wrap(barOfTick(tick, tempo), ELEMENTS);
}

export const cycle: AttackModule<CycleAttack> = {
  reads: [
    'attackCooldownMul',
    'damageMul',
    'elementBeatsAdd',
    'elementSpreadAdd',
    'pierceAdd',
    'projectileCountAdd',
    'projectileSpeedMul',
  ],

  step(ctx, player, attack, input) {
    const { state } = ctx;
    const count = Math.max(1, Math.floor(statValue(player, 'projectileCountAdd', 1)));
    const free = keepFamiliars(ctx, player, attack, count).filter(
      (familiar) => familiar.phase !== 'flying',
    );
    const prey = input.fire ? preyOf(state.enemies, player, attack.reach) : [];
    if (!pullTrigger(player, prey.length > 0 && free.length > 0, attack.cooldownTicks)) {
      return;
    }
    free.forEach((familiar, index) => {
      const target = prey[index % prey.length];
      if (target !== undefined) {
        loopOut(ctx, player, attack, familiar, target);
      }
    });
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const { state } = ctx;
    const props = state.props;
    if (props === undefined) {
      return;
    }
    let gone = false;
    for (const prop of props) {
      if (prop.kind === BURN) {
        smoulder(ctx, prop);
        gone ||= prop.ticksLeft <= 0;
      }
    }
    for (const owner of state.players) {
      familiarsOf(state, owner).forEach((familiar, slot, all) => {
        fly(ctx, owner, familiar, slot, all.length);
      });
    }
    if (gone) {
      keepWhere(props, (prop) => prop.kind !== BURN || prop.ticksLeft > 0);
      if (props.length === 0) {
        delete state.props;
      }
    }
  },

  reach(attack) {
    return attack.reach;
  },
};

function familiarsOf(state: SimState, owner: Pick<PlayerState, 'id'>): PropState[] {
  return (state.props ?? []).filter((prop) => prop.kind === FAMILIAR && prop.playerId === owner.id);
}

function keepFamiliars(
  { state, tempo }: StepContext,
  player: PlayerState,
  attack: CycleAttack,
  count: number,
): PropState[] {
  const familiars = familiarsOf(state, player);
  while (familiars.length < count) {
    const at = circling(player, attack, familiars.length, count, state.tick, tempo);
    const made: PropState = {
      id: state.nextEntityId,
      kind: FAMILIAR,
      playerId: player.id,
      phase: 'held',
      x: at.x,
      y: at.y,
      prevX: at.x,
      prevY: at.y,
      vx: 0,
      vy: 0,
      damage: 0,
      ticksLeft: 0,
      hitsLeft: 0,
      rank: elementOfTick(state.tick, tempo),
    };
    state.nextEntityId += 1;
    (state.props ??= []).push(made);
    familiars.push(made);
  }
  if (familiars.length > count) {
    const extra = familiars.slice(count);
    const props = state.props ?? [];
    keepWhere(props, (prop) => !extra.includes(prop));
  }
  return familiars.slice(0, count);
}

// Its place on the slow circle round him, shared out between his familiars.
function circling(
  owner: Vec2,
  attack: CycleAttack,
  slot: number,
  count: number,
  tick: number,
  tempo: Tempo,
): Vec2 {
  const turns =
    slot / count + (tick - tempo.originTick) / (attack.orbit.beats * tempo.ticksPerBeat);
  const way = unitFromAngle(2 * Math.PI * turns - Math.PI / 2);
  return { x: owner.x + way.x * attack.orbit.radius, y: owner.y + way.y * attack.orbit.radius };
}

function legTicks(
  owner: PlayerState,
  attack: CycleAttack,
  beats: CycleAttack['outBeats'],
  from: Vec2,
  to: Vec2,
  tempo: Tempo,
): number {
  const far = clamp01(Math.sqrt(distanceSquared(from, to)) / attack.reach);
  const speed = statValue(owner, 'projectileSpeedMul', 1);
  return Math.max(2, Math.round((lerp(beats[0], beats[1], far) * tempo.ticksPerBeat) / speed));
}

function loopOut(
  ctx: StepContext,
  owner: PlayerState,
  attack: CycleAttack,
  familiar: PropState,
  target: EnemyState,
): void {
  const { state, tempo } = ctx;
  const ticks = legTicks(owner, attack, attack.outBeats, familiar, target, tempo);
  const element = elementOfTick(state.tick + ticks - 1, tempo);
  familiar.phase = 'flying';
  familiar.targetId = target.id;
  familiar.damage = statValue(owner, 'damageMul', attack.damage);
  familiar.hitsLeft =
    (attack.passes[element] ?? 1) + Math.max(0, Math.floor(statValue(owner, 'pierceAdd', 0)));
  familiar.hitIds = [];
  setOff(familiar, target, owner, attack, ticks);
}

function loopBack(
  ctx: StepContext,
  owner: PlayerState,
  attack: CycleAttack,
  familiar: PropState,
  slot: number,
  count: number,
): void {
  const { state, tempo } = ctx;
  const near = circling(owner, attack, slot, count, state.tick, tempo);
  const ticks = legTicks(owner, attack, attack.backBeats, familiar, near, tempo);
  familiar.phase = 'returning';
  delete familiar.targetId;
  setOff(
    familiar,
    circling(owner, attack, slot, count, state.tick + ticks, tempo),
    owner,
    attack,
    ticks,
  );
}

// A leg bends on the side away from him, wide enough to clear his flags.
function setOff(
  familiar: PropState,
  to: Vec2,
  owner: Vec2,
  attack: CycleAttack,
  ticks: number,
): void {
  const dx = to.x - familiar.x;
  const dy = to.y - familiar.y;
  const middle = { x: (familiar.x + to.x) / 2, y: (familiar.y + to.y) / 2 };
  const left = { x: middle.x - dy * BULGE, y: middle.y + dx * BULGE };
  const right = { x: middle.x + dy * BULGE, y: middle.y - dx * BULGE };
  const bend = distanceSquared(left, owner) >= distanceSquared(right, owner) ? left : right;
  const halfway = {
    x: (familiar.x + 2 * bend.x + to.x) / 4,
    y: (familiar.y + 2 * bend.y + to.y) / 4,
  };
  const away = Math.sqrt(distanceSquared(halfway, owner));
  const clear = attack.orbit.radius;
  if (away > 0 && away < clear) {
    bend.x += ((halfway.x - owner.x) / away) * 2 * (clear - away);
    bend.y += ((halfway.y - owner.y) / away) * 2 * (clear - away);
  }
  familiar.vx = (2 * (bend.x - familiar.x)) / ticks;
  familiar.vy = (2 * (bend.y - familiar.y)) / ticks;
  familiar.ticksLeft = ticks;
}

// One tick along the curve that leaves where it is, as it is going, and reaches `to` in its ticks left.
function glide(familiar: PropState, to: Vec2): void {
  const left = Math.max(1, familiar.ticksLeft);
  const bendX = familiar.x + (familiar.vx * left) / 2;
  const bendY = familiar.y + (familiar.vy * left) / 2;
  const s = 1 / left;
  familiar.vx = (2 * (1 - s) * (bendX - familiar.x) + 2 * s * (to.x - bendX)) / left;
  familiar.vy = (2 * (1 - s) * (bendY - familiar.y) + 2 * s * (to.y - bendY)) / left;
  familiar.x = (1 - s) * (1 - s) * familiar.x + 2 * s * (1 - s) * bendX + s * s * to.x;
  familiar.y = (1 - s) * (1 - s) * familiar.y + 2 * s * (1 - s) * bendY + s * s * to.y;
  familiar.ticksLeft = left - 1;
}

function fly(
  ctx: StepContext,
  owner: PlayerState,
  familiar: PropState,
  slot: number,
  count: number,
): void {
  const { state, tempo } = ctx;
  const attack = cycleOf(ctx, owner);
  if (familiar.phase === 'held') {
    const at = circling(owner, attack, slot, count, state.tick, tempo);
    familiar.x = at.x;
    familiar.y = at.y;
    return;
  }
  if (familiar.phase === 'returning') {
    glide(familiar, circling(owner, attack, slot, count, state.tick + familiar.ticksLeft, tempo));
    brush(ctx, owner, attack, familiar, 0);
    if (familiar.ticksLeft <= 0) {
      familiar.phase = 'held';
      delete familiar.hitIds;
    }
    return;
  }
  const target = state.enemies.find((enemy) => enemy.id === familiar.targetId && enemy.hp > 0);
  if (target === undefined) {
    loopBack(ctx, owner, attack, familiar, slot, count);
    return;
  }
  glide(familiar, target);
  brush(ctx, owner, attack, familiar, 1);
  if (familiar.ticksLeft <= 0) {
    hit(ctx, owner, familiar, target);
    loopBack(ctx, owner, attack, familiar, slot, count);
  }
}

// What it brushes on its way, keeping a hit for the bad vibe it loops out to.
function brush(
  ctx: StepContext,
  owner: PlayerState,
  attack: CycleAttack,
  familiar: PropState,
  kept: number,
): void {
  for (const enemy of ctx.state.enemies) {
    if (familiar.hitsLeft <= kept) {
      return;
    }
    if (
      enemy.hp > 0 &&
      enemy.id !== familiar.targetId &&
      familiar.hitIds?.includes(enemy.id) !== true &&
      touches(enemy, familiar, attack.radius)
    ) {
      hit(ctx, owner, familiar, enemy);
    }
  }
}

function hit(ctx: StepContext, owner: PlayerState, familiar: PropState, enemy: EnemyState): void {
  const element = elementOfTick(ctx.state.tick, ctx.tempo);
  const from = { x: familiar.prevX, y: familiar.prevY };
  familiar.rank = element;
  (familiar.hitIds ??= []).push(enemy.id);
  familiar.hitsLeft -= 1;
  spread(ctx, owner, familiar, element, enemy, from);
  strike(ctx, owner, element, enemy, familiar.damage, from);
}

function cycleOf(ctx: Pick<StepContext, 'content'>, owner: PlayerState): CycleAttack {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  if (attack?.kind !== 'cycle') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no elements`);
  }
  return attack;
}

export function isSmall(ctx: Pick<StepContext, 'content'>, enemy: EnemyState): boolean {
  if (enemy.isBoss) {
    return false;
  }
  const behaviour = ctx.content.enemies.get(enemy.kind)?.behaviour;
  return behaviour === 'horde' || behaviour === 'rusher';
}

function heldBeats(owner: PlayerState, beats: number): number {
  return beats + Math.max(0, Math.floor(statValue(owner, 'elementBeatsAdd', 0)));
}

export function strike(
  ctx: StepContext,
  owner: PlayerState,
  element: number,
  enemy: EnemyState,
  damage: number,
  from: Vec2,
): void {
  const { state, tempo } = ctx;
  const attack = cycleOf(ctx, owner);
  hurtEnemy(state, enemy, damage, owner.id, from);
  if (enemy.hp <= 0) {
    return;
  }
  switch (element) {
    case FIRE:
      ignite(
        state,
        owner,
        enemy,
        statValue(owner, 'damageMul', attack.burn.damage),
        heldBeats(owner, attack.burn.beats) * tempo.ticksPerBeat,
      );
      return;
    case WATER:
      applyEnemyStatus(
        state,
        enemy,
        'slowed',
        heldBeats(owner, attack.slowBeats) * tempo.ticksPerBeat,
      );
      return;
    case AIR:
      if (enemy.isBoss) {
        applyEnemyStatus(state, enemy, 'slowed', tempo.ticksPerBeat);
      } else if (isSmall(ctx, enemy)) {
        knockBack(enemy, from, (attack.knockback * heldBeats(owner, 2)) / 2, ctx.enemyObstacles);
        keepInside(state, enemy);
      }
      return;
    default:
      applyEnemyStatus(
        state,
        enemy,
        'frozen',
        heldBeats(owner, attack.frozenBeats) * tempo.ticksPerBeat,
      );
  }
}

function ignite(
  state: SimState,
  owner: PlayerState,
  enemy: EnemyState,
  damage: number,
  ticks: number,
): void {
  const props = (state.props ??= []);
  const lit = props.find(
    (prop) => prop.kind === BURN && prop.targetId === enemy.id && prop.playerId === owner.id,
  );
  if (lit !== undefined) {
    lit.ticksLeft = Math.max(lit.ticksLeft, ticks);
    lit.damage = Math.max(lit.damage, damage);
    return;
  }
  props.push({
    id: state.nextEntityId,
    kind: BURN,
    playerId: owner.id,
    phase: 'grounded',
    x: enemy.x,
    y: enemy.y,
    prevX: enemy.x,
    prevY: enemy.y,
    vx: 0,
    vy: 0,
    damage,
    ticksLeft: ticks,
    hitsLeft: 0,
    targetId: enemy.id,
  });
  state.nextEntityId += 1;
}

function smoulder(ctx: StepContext, burn: PropState): void {
  const { state, tempo } = ctx;
  const enemy = state.enemies.find((candidate) => candidate.id === burn.targetId);
  if (enemy === undefined || enemy.hp <= 0) {
    burn.ticksLeft = 0;
    return;
  }
  burn.x = enemy.x;
  burn.y = enemy.y;
  burn.ticksLeft -= 1;
  if (isBeatTick(state.tick, tempo)) {
    hurtEnemy(state, enemy, burn.damage, burn.playerId, null);
  }
}

function spread(
  ctx: StepContext,
  owner: PlayerState,
  familiar: PropState,
  element: number,
  struck: EnemyState,
  from: Vec2,
): void {
  const quake = element === EARTH ? cycleOf(ctx, owner).quake : 0;
  const around = statValue(owner, 'elementSpreadAdd', quake);
  if (around <= 0) {
    return;
  }
  const hitIds = (familiar.hitIds ??= []);
  for (const enemy of ctx.state.enemies) {
    const reach = around + enemy.radius + struck.radius;
    if (
      enemy !== struck &&
      enemy.hp > 0 &&
      !hitIds.includes(enemy.id) &&
      distanceSquared(enemy, struck) <= reach * reach
    ) {
      hitIds.push(enemy.id);
      strike(ctx, owner, element, enemy, familiar.damage, from);
    }
  }
}

export function keepInside(state: SimState, body: { x: number; y: number }): void {
  body.x = clamp(body.x, 0, state.arena.width);
  body.y = clamp(body.y, 0, state.arena.height);
}
